import React from 'react';
import classes from './AuctionContent.module.scss';
import Loading from '../Layout/Loading';
import EmptyContentCard from '../Layout/EmptyContentCard';
import { Button, Tooltip } from '@chakra-ui/react';
import { formatDate, formatDateWithTime } from '../../helper/utils';
import FilterAccordion from './FilterAccordion';
import SavedSearches from './SavedSearches';
import { auctionSavedSearchAdapter, auctionCurrentSearch } from './auctionSavedSearches';
import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material/styles';
import placeholderImage from '../../media/images/emptyFavorites.png';

// Create a complete MUI theme with required properties
const mobileMuiTheme = createTheme({
  palette: {
    primary: {
      main: '#7D61F0',
    },
    secondary: {
      main: '#4A0CB6',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#f5f5f5',
      A200: '#eeeeee',
      A400: '#bdbdbd',
      A700: '#616161',
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    body1: {
      fontSize: '14px',
      fontWeight: 400,
    },
    body2: {
      fontSize: '12px',
      fontWeight: 400,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600,
    },
  },
  components: {
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          borderRadius: '16px',
          border: '1px solid #e0e0e0',
          minWidth: '300px',
          padding: 0,
          marginTop: '8px'
        }
      }
    }
  }
});

// FilterParameters array
const filterParameters = [
  {
    key: "maker_name",
    icon: 'fas fa-car',
    label: "Make",
    options: ['BMW','EUNOS','HONDA','MAZDA','MITSUBISHI','NISSAN','PORSCHE','SUBARU','SUZUKI','TOYOTA'],
  },
  {
    key: "model",  // Leave as "model" for backend compatibility
    modelKey: "makerDetail.type", // Use this key when accessing data in the frontend
    icon: 'fas fa-box',
    label: "Model",
    options: [] // Empty array to be populated by API data
  },
  {
    key: "grade",
    icon: 'far fa-star',
    label: "Grade",
    options: [4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1]
  },
  {
    key: "year",
    icon: 'far fa-calendar',
    label: "Year",
    options: [1960, 2024]
  },
  {
    key: "color",
    icon: 'fas fa-palette',
    label: "Color",
    options: ['#000', '#4E5BA6', '#47CD89', '#FF0000', '#FDB022', '#669F2A', '#F9DBAF', 'Orange', 'Purple', 'Pink']
  },
  {
    key: "transmission",
    icon: 'fas fa-cog',
    label: "Transmission",
    options: ['Automatic', 'Manual']
  },
  {
    key: "bidDate",
    icon: 'far fa-calendar-alt',
    label: "Bid Date",
    options: ['28/09/2024', '29/09/2024', '30/09/2024', '01/10/2024', '02/10/2024', '03/10/2024', '04/10/2024', '05/10/2024', '06/10/2024', '07/10/2024']
  }
];

// Helper to format bid number
const formatBidNumber = (bidNo) => {
  if (!bidNo) return "";
  // Clean up the bid number by removing the "No." prefix if it exists
  return bidNo.replace(/^No\./, '');
};

const MobileAuctionContent = (props) => {
  const {
    carsData, 
    loading, 
    initialLoading, 
    totalCars, 
    isMobile, 
    page, 
    setPage, 
    viewMode, 
    handleViewModeChange, 
    sortBy, 
    setSortBy, 
    onCardClickHandler, 
    onClickFavouriteHandler, 
    getCarName, 
    localFavorites, 
    scrollContainerRef, 
    handleScroll,
    userData,
    isFreeTier,
    onClickFilterHandler,
    isMobileFilterOpen,
    filterString,
    filterHandler,
    carFiltersList,
    filterAnimationState,
    clearCarList,
    selectedSavedSearchFilter,
    setSelectedSavedSearchFilter,
    savedSearchFilters,
    setSavedSearchFilters,
    scheduleDay,
    searchText,
    onClearSearch,
    onApplySavedSearch
  } = props;

  // Picking a saved search closes the drawer so the matching cars are what you see next.
  const applySavedSearchAndClose = (savedSearch) => {
    onApplySavedSearch(savedSearch);
    onClickFilterHandler();
  };

  // Mobile view JSX will go here
  return (
    <div className={classes.auctionCarsContainer}>
      <div className={classes.auctionCarsHeader}>
        <h1>
          {totalCars > 0 ? `${totalCars} ${totalCars === 1 ? 'vehicle' : 'vehicles'} found` : 'No vehicles found'}
        </h1>
        <div className={classes.headerControls}>
            <div className={classes.controlButtons}>
              <div className={classes.viewToggle}>
                <Tooltip label="Grid View" placement="top">
                    <Button 
                        variant={viewMode === 'grid' ? 'solid' : 'outline'} 
                        colorScheme="purple" 
                        size="sm" 
                        onClick={() => handleViewModeChange('grid')}
                        marginRight="8px"
                    >
                        <i className="fas fa-th-large"></i>
                    </Button>
                </Tooltip>
                <Tooltip label="List View" placement="top">
                    <Button 
                        variant={viewMode === 'list' ? 'solid' : 'outline'} 
                        colorScheme="purple" 
                        size="sm" 
                        onClick={() => handleViewModeChange('list')}
                        marginRight="8px"
                    >
                        <i className="fas fa-list"></i>
                    </Button>
                </Tooltip>
              </div>
              <Tooltip label="Filter" placement="top">
                  <Button 
                      variant={isMobileFilterOpen ? 'solid' : 'outline'} 
                      colorScheme="purple" 
                      size="sm" 
                      onClick={onClickFilterHandler}
                  >
                      <i className="fas fa-filter"></i>
                  </Button>
              </Tooltip>
            </div>
            <div className={classes.auctionCarsHeaderSort}>
              <p>Sort by:</p>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} >
                  <option value="default">Default (Newest)</option>
                  <option value="km">Km (Lowest)</option>
                  <option value="kmDesc">Km (Highest)</option>
                  <option value="bidDate">Bidding Date (Earliest)</option>
                  <option value="bidDateDesc">Bidding Date (Latest)</option>
                  <option value="grade">Grade (Highest)</option>
                  <option value="gradeDesc">Grade (Lowest)</option>
                  <option value="bidNo">Bid No (Lowest)</option>
                  <option value="bidNoDesc">Bid No (Highest)</option>
              </select>
            </div>
        </div>
      </div>

      {/* Mobile Filter Backdrop */}
      {isMobileFilterOpen && (
        <div 
          className={`${classes.filterBackdrop} ${filterAnimationState ? classes[filterAnimationState] : ''}`}
          onClick={onClickFilterHandler}
        />
      )}

      {/* Mobile Filter Drawer */}
      {isMobileFilterOpen && (
        <div className={`${classes.mobileFilter} ${filterAnimationState ? classes[filterAnimationState] : ''}`}>
          <div className={classes.filterHeader}>
            <div className={classes.filterHeaderTitle}>
              <i className="fas fa-filter"></i>
              <h3>Filters</h3>
            </div>
            <div className={classes.filterHeaderActions}>
              <button className={classes.closeButton} onClick={onClickFilterHandler} aria-label="Close filters">
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
          <MUIThemeProvider theme={mobileMuiTheme}>
            <div className={classes.mobileFilterContent}>
              {carFiltersList && (
                <FilterAccordion
                  items={filterParameters}
                  filterString={filterString}
                  setFilterString={filterHandler}
                  reloadCarList={clearCarList}
                  carFiltersList={carFiltersList}
                  selectedSavedSearchFilter={selectedSavedSearchFilter}
                  setSelectedSavedSearchFilter={setSelectedSavedSearchFilter}
                  scheduleDay={scheduleDay}
                  searchText={searchText}
                  onClearSearch={onClearSearch}
                  savedSearchesSlot={
                    <SavedSearches
                      savedSearches={savedSearchFilters}
                      setSavedSearches={setSavedSearchFilters}
                      adapter={auctionSavedSearchAdapter}
                      current={auctionCurrentSearch(filterString, searchText)}
                      onApply={applySavedSearchAndClose}
                    />
                  }
                />
              )}
            </div>
          </MUIThemeProvider>
        </div>
      )}

      {/* Content container that will scroll */}
      <div className={classes.scrollableContentContainer}>
        {/* Mobile Grid View */}
        {viewMode === 'grid' && (
          <div className={classes.auctionCars}
            ref={scrollContainerRef} 
            onScroll={handleScroll}
          >
            {initialLoading ? (
                <div className={classes.loadingContainer}>
                    <Loading text="Cars" />
                </div>
            ) : carsData.length > 0 ? (
              <>
                {carsData.map((content, index) => (
                  <div 
                    key={index} 
                    className={classes.auctionCard}
                    onClick={() => onCardClickHandler(content)}
                  >
                    <div className={classes.carImageContainer}>
                        <div className={`${classes.favCarIcon} ${(content?.isFavorite || localFavorites.has(content._id)) && classes.isFav}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClickFavouriteHandler(content);
                            }}>
                                {(content?.isFavorite || localFavorites.has(content._id)) ? 
                                    <i className="fas fa-heart"></i> : 
                                    <i className="far fa-heart"></i>
                                }
                            </div>
                        <img 
                            src={content.vehicleImages && content.vehicleImages.length > 0 ? content.vehicleImages[0] : placeholderImage} 
                            alt="car"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = placeholderImage;
                            }}
                        />
                        <div className={classes.Bid}>
                            <p>Bid No: {formatBidNumber(content.bidNo)}</p>
                        </div>
                    </div>
                    <div className={classes.cardTextContent}>
                        {/* The bid number sits on the photo, as on desktop; two
                            pills side by side don't fit a phone's half-width card. */}
                        <div className={classes.infoRow}>
                            <div className={classes.kmDiv}>
                                <p>● {content.mileage}</p>
                            </div>
                        </div>
                        <h3>{getCarName(content)}</h3>
                        <div className={classes.auctionCarDetails}>
                            <div className={classes.auctionCarText}>
                                <div className={classes.IconContainer}>
                                    <i className="far fa-star"></i>
                                </div>
                                <p>Grade:</p> 
                                <span>{content.inspectionScore || "none"}</span>
                            </div>
                            <div className={classes.auctionCarText}>
                                <div className={classes.IconContainer}>
                                    <i className="fas fa-gavel"></i>
                                </div>
                                <p>Auction:</p>
                                <span>{content.bidDetail?.bidSite}</span>
                            </div>
                            <div className={classes.auctionCarText}>
                                <div className={classes.IconContainer}>
                                    <i className="far fa-calendar"></i>
                                </div>
                                <p>Bidding Date:</p>
                                <span>{formatDate(content.bidDetail?.bidDate)}</span>
                            </div>
                            <div className={classes.auctionCarText}>
                                <div className={classes.IconContainer}>
                                    <i className="fas fa-cog"></i>
                                </div>
                                <p>Transmission:</p>
                                <span>{content.engineDetail.transmission}</span>
                            </div>
                        </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className={classes.paginationLoadingContainer}>
                    <Loading text="Loading more" />
                  </div>
                )}
              </>
            ) : (
              <div className={classes.loadingContainer}>
                <EmptyContentCard header="No Cars Available" message="Looks like there are no results for this search query"/>
              </div>
            )}
          </div>
        )}

        {/* Mobile List View */}
        {viewMode === 'list' && (
          <div className={classes.mobileList} 
              ref={scrollContainerRef} 
              onScroll={handleScroll}
          >
            <div className={classes.mobileListHeader}>
              <span>Details</span>
              {!isFreeTier && <span className={classes.vipBadge}>VIP</span>}
            </div>
            
            {initialLoading ? (
              <div className={classes.loadingContainer}><Loading text="Cars" /></div>
            ) : carsData.length > 0 ? (
              <>
                {carsData.map((content, index) => (
                  <div key={index} className={classes.mobileListItem} onClick={() => onCardClickHandler(content)}>
                    <div className={classes.mobileListImageContainer}>
                      <img 
                        src={content.vehicleImages && content.vehicleImages.length > 0 ? content.vehicleImages[0] : placeholderImage} 
                        alt={getCarName(content)}
                        className={classes.mobileListImage}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = placeholderImage;
                        }}
                      />
                      <div className={`${classes.mobileListFav} ${(content?.isFavorite || localFavorites.has(content._id)) && classes.isFav}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onClickFavouriteHandler(content);
                        }}>
                        {(content?.isFavorite || localFavorites.has(content._id)) ? 
                          <i className="fas fa-heart"></i> : 
                          <i className="far fa-heart"></i>
                        }
                      </div>
                    </div>
                    
                    <div className={classes.mobileListContent}>
                      <div className={classes.mobileListTitle}>
                        {getCarName(content)}
                      </div>
                      
                      <div className={classes.mobileListInfoRow}>
                        <div className={classes.mobileListBid}>
                          Bid: {formatBidNumber(content.bidNo)}
                        </div>
                        <div className={classes.mobileListMileage}>
                          {content.mileage}
                        </div>
                      </div>
                      
                      <div className={classes.mobileListDetailsRow}>
                        <div className={classes.mobileListDetail}>
                          <i className="far fa-star"></i> {content.inspectionScore || "N/A"}
                        </div>
                        <div className={classes.mobileListDetail}>
                          <i className="fas fa-gavel"></i> {content.bidDetail?.bidSite}
                        </div>
                        <div className={classes.mobileListDetail}>
                          <i className="fas fa-cog"></i> {content.engineDetail.transmission}
                        </div>
                      </div>
                      
                      <div className={classes.mobileListDateRow}>
                        <div className={classes.mobileListDate}>
                          <i className="far fa-calendar"></i> 
                          {isFreeTier ? formatDate(content.bidDetail?.bidDate) : formatDateWithTime(content.bidDetail?.bidDate, content.auctionTime)}
                        </div>
                        {!isFreeTier && content.auctionTime && (
                          <div className={classes.userTimeZone}>
                            Your time: {(() => {
                              try {
                                // From the utils.js, we can see date format is YYYY/MM/DD
                                const dateParts = content.bidDetail.bidDate.split('/');
                                if (dateParts.length !== 3) return 'Invalid format';
                                
                                // Create ISO date string
                                const dateString = `${dateParts[0]}-${dateParts[1].padStart(2, '0')}-${dateParts[2].padStart(2, '0')}T${content.auctionTime}:00+09:00`;
                                const date = new Date(dateString);
                                
                                // Check if date is valid
                                if (isNaN(date.getTime())) return 'Check auction details';
                                
                                // Format date with MM/DD/YYYY format
                                const formattedDate = `${(date.getMonth()+1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
                                
                                // Get time with AM/PM
                                const formattedTime = date.toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                });
                                
                                // Get timezone abbreviation
                                const timeZone = date.toLocaleTimeString([], {
                                  timeZoneName: 'short'
                                }).split(' ').pop();
                                
                                return `${formattedDate}, ${formattedTime} ${timeZone}`;
                              } catch (e) {
                                return 'Check auction details';
                              }
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className={classes.paginationLoadingContainer}>
                    <Loading text="Loading more" />
                  </div>
                )}
              </>
            ) : (
              <div className={classes.loadingContainer}>
                <EmptyContentCard header="No Cars Available" message="Looks like there are no results for this search query"/>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Load More Button - Now part of the main container but outside the scroll area */}
      {!loading && !initialLoading && carsData.length > 0 && carsData.length < totalCars && (
        <div className={classes.fixedLoadMoreButtonContainer}>
          <Button
            colorScheme="purple"
            size="md"
            onClick={() => setPage(page + 1)}
            isLoading={loading}
            loadingText="Loading"
            width="200px"
            bgColor="#4A0CB6"
            color="white"
            _hover={{ bgColor: "#7D61F0" }}
            fontFamily="Inter, sans-serif"
          >
            Load More ({carsData.length} of {totalCars})
          </Button>
        </div>
      )}
    </div>
  );
};

export default MobileAuctionContent; 